import type { Asset, MapGeometry, MediaGrid, PlayerAsset, Point, Scene } from "../types";
import { mediaCoordinateBounds } from "./scene-coordinates";
import { normalizeMediaRotation, rotateMediaPoint, rotatedMediaBounds } from "./media-rotation";

type MediaAsset = Pick<Asset | PlayerAsset, "width" | "height">;
type PlayableScene = Pick<Scene, "type" | "geometry" | "playableMap">;

export const MEDIA_MAP_DEFAULT_CELLS_ACROSS = 24;
export const MEDIA_MAP_MIN_CELLS_ACROSS = 4;
export const MEDIA_MAP_MAX_CELLS_ACROSS = 120;

/**
 * The source span is intentionally separate from a grid cell's physical
 * dimension. Raising this number makes the media occupy more real table
 * space (and therefore appear closer) while the calibrated inch per cell
 * remains unchanged.
 */
export function clampMediaMapCellsAcross(value: number | undefined, fallback = MEDIA_MAP_DEFAULT_CELLS_ACROSS) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.round(Math.max(MEDIA_MAP_MIN_CELLS_ACROSS, Math.min(MEDIA_MAP_MAX_CELLS_ACROSS, numeric)));
}

export function mediaMapCellsAcross(scene: Pick<Scene, "geometry" | "mediaGrid">): number {
  return clampMediaMapCellsAcross(scene.geometry?.width ?? scene.mediaGrid?.cellsAcross);
}

/** True for a scene whose source is an imported still or motion picture. */
export function isMediaScene(scene: Pick<Scene, "type"> | undefined): boolean {
  return scene?.type === "image" || scene?.type === "video";
}

/**
 * A playable media map is still typed as image/video so it retains its media
 * controls, but it is rendered through the normal map pipeline.
 */
export function isPlayableMapScene<T extends PlayableScene>(scene: T | undefined): scene is T & { geometry: MapGeometry } {
  return Boolean(scene?.geometry && (scene.type === "map" || (isMediaScene(scene) && scene.playableMap === true)));
}

export function isPlayableMediaMap<T extends PlayableScene>(scene: T | undefined): scene is T & { geometry: MapGeometry; playableMap: true } {
  return Boolean(scene && isMediaScene(scene) && scene.playableMap === true && scene.geometry);
}

function finite(value: number | undefined, fallback: number) {
  return Number.isFinite(value) && (value ?? 0) > 0 ? value! : fallback;
}

function mapGeometryForMedia(bounds: { width: number; height: number }, pixelsAcross: number | undefined, grid?: MediaGrid): MapGeometry {
  const width = clampMediaMapCellsAcross(grid?.cellsAcross);
  const height = Math.max(4, Math.round(width * bounds.height / bounds.width * 2) / 2);
  const gridType = grid?.type === "honeycomb" ? "hex" : "square";
  return {
    width,
    height,
    grid: {
      size: 1,
      type: gridType,
      color: grid?.color ?? "#f4e8c4",
      opacity: grid?.opacity ?? 0.32,
      offset: { x: 0, y: 0 },
    },
    walls: [],
    objectWalls: [],
    portals: [],
    lights: [],
    ambientLight: 0.55,
    source: {
      format: "image",
      pixelsPerGrid: finite(pixelsAcross, width * 80) / width,
      warnings: [],
    },
  };
}

function scalePoint(point: Point, scaleX: number, scaleY: number): Point {
  return { x: point.x * scaleX, y: point.y * scaleY };
}

function scalePolygonList(polygons: Point[][] | undefined, scaleX: number, scaleY: number) {
  return polygons?.map((polygon) => polygon.map((point) => scalePoint(point, scaleX, scaleY)));
}

/**
 * Promote an imported still or video to a normal tabletop playfield. Existing
 * media annotations are converted from the media's old 100-unit coordinates
 * to the new calibrated grid, so enabling map play never drops their layout.
 */
export function enableMediaMap(scene: Scene, asset?: MediaAsset): Scene {
  if (!isMediaScene(scene)) return scene;
  if (scene.geometry) return { ...scene, playableMap: true };

  const sourceBounds = mediaCoordinateBounds(asset);
  const rotation = scene.type === "image" ? normalizeMediaRotation(scene.view.rotation) : 0;
  const displayBounds = rotatedMediaBounds(sourceBounds, rotation);
  const pixelsAcross = rotation === 90 || rotation === 270 ? asset?.height : asset?.width;
  const geometry = mapGeometryForMedia(displayBounds, pixelsAcross, scene.mediaGrid);
  const scaleX = geometry.width / displayBounds.width;
  const scaleY = geometry.height / displayBounds.height;
  const transformPoint = (point: Point) => scalePoint(rotateMediaPoint(point, sourceBounds, rotation), scaleX, scaleY);
  const scaleFog = {
    ...scene.fog,
    operations: scene.fog.operations.map((operation) => ({ ...operation, points: operation.points.map(transformPoint), radius: operation.radius === undefined ? undefined : operation.radius * Math.min(scaleX, scaleY) })),
    explored: scalePolygonList(scene.fog.explored, scaleX, scaleY) ?? [],
  };

  return {
    ...scene,
    playableMap: true,
    geometry,
    mapPins: scene.mapPins?.map((pin) => ({ ...pin, position: transformPoint(pin.position) })),
    measurements: scene.measurements?.map((measurement) => ({ ...measurement, a: transformPoint(measurement.a), b: transformPoint(measurement.b) })),
    areaTemplates: scene.areaTemplates?.map((template) => ({ ...template, a: transformPoint(template.a), b: transformPoint(template.b) })),
    tokens: scene.tokens.map((token) => ({ ...token, position: transformPoint(token.position), size: token.size * Math.min(scaleX, scaleY), visionRadius: token.visionRadius * Math.min(scaleX, scaleY) })),
    initialTokenPositions: scene.initialTokenPositions && Object.fromEntries(Object.entries(scene.initialTokenPositions).map(([id, point]) => [id, transformPoint(point)])),
    playerView: scene.playerView && { center: transformPoint(scene.playerView.center) },
    cameraBookmarks: scene.cameraBookmarks?.map((bookmark) => ({ ...bookmark, center: transformPoint(bookmark.center) })),
    fog: scaleFog,
    waterRegions: scene.waterRegions?.map((region) => ({ ...region, points: region.points.map(transformPoint), polygons: scalePolygonList(region.polygons, scaleX, scaleY), flowPaths: scalePolygonList(region.flowPaths, scaleX, scaleY), erasePolygons: scalePolygonList(region.erasePolygons, scaleX, scaleY), brushRadii: region.brushRadii?.map((radius) => radius * Math.min(scaleX, scaleY)) })),
    weatherDryRegions: scene.weatherDryRegions?.map((region) => ({ ...region, points: region.points.map(transformPoint), polygons: scalePolygonList(region.polygons, scaleX, scaleY) })),
    materialRegions: scene.materialRegions?.map((region) => ({ ...region, points: region.points.map(transformPoint), polygons: scalePolygonList(region.polygons, scaleX, scaleY) })),
    physicalObjects: scene.physicalObjects?.map((object) => ({ ...object, position: transformPoint(object.position) })),
  };
}

/**
 * Resize a playable image or video map in source space. Map-relative anchors
 * move with the background, but token footprints and vision remain expressed
 * in grid cells. This keeps a default 1-square token exactly inside one
 * calibrated cell after changing the media span. `gridInches` and
 * physical-object measurements are likewise deliberately left intact so a
 * calibrated cell remains the same real-world inch size on the projector.
 */
export function resizePlayableMediaMap(scene: Scene, cellsAcross: number): Scene {
  if (!isPlayableMediaMap(scene)) return scene;
  const currentWidth = Math.max(0.001, scene.geometry.width);
  const nextWidth = clampMediaMapCellsAcross(cellsAcross);
  if (Math.abs(nextWidth - currentWidth) < 0.001) return scene;

  const scale = nextWidth / currentWidth;
  const transformPoint = (point: Point) => scalePoint(point, scale, scale);
  const geometry = scene.geometry;
  // Stored mask assets are rasterized in the old coordinate space. The source
  // fog operations remain authoritative, so drop only that derived cache.
  const { exploredMaskRef: _discardedExploredMask, ...fogWithoutMask } = scene.fog;

  return {
    ...scene,
    view: { ...scene.view, x: scene.view.x * scale, y: scene.view.y * scale },
    geometry: {
      ...geometry,
      width: nextWidth,
      height: geometry.height * scale,
      grid: { ...geometry.grid, offset: transformPoint(geometry.grid.offset) },
      walls: geometry.walls.map((wall) => ({ ...wall, a: transformPoint(wall.a), b: transformPoint(wall.b) })),
      objectWalls: geometry.objectWalls.map((wall) => ({ ...wall, a: transformPoint(wall.a), b: transformPoint(wall.b) })),
      portals: geometry.portals.map((portal) => ({ ...portal, a: transformPoint(portal.a), b: transformPoint(portal.b) })),
      lights: geometry.lights.map((light) => ({ ...light, position: transformPoint(light.position), radius: light.radius * scale })),
      source: { ...geometry.source, pixelsPerGrid: geometry.source.pixelsPerGrid === undefined ? undefined : geometry.source.pixelsPerGrid / scale },
    },
    mediaGrid: scene.mediaGrid ? { ...scene.mediaGrid, cellsAcross: nextWidth } : undefined,
    tokens: scene.tokens.map((token) => ({ ...token, position: transformPoint(token.position) })),
    initialTokenPositions: scene.initialTokenPositions && Object.fromEntries(Object.entries(scene.initialTokenPositions).map(([id, point]) => [id, transformPoint(point)])),
    playerView: scene.playerView && { center: transformPoint(scene.playerView.center) },
    cameraBookmarks: scene.cameraBookmarks?.map((bookmark) => ({ ...bookmark, center: transformPoint(bookmark.center) })),
    mapPins: scene.mapPins?.map((pin) => ({ ...pin, position: transformPoint(pin.position) })),
    measurements: scene.measurements?.map((measurement) => ({ ...measurement, a: transformPoint(measurement.a), b: transformPoint(measurement.b) })),
    areaTemplates: scene.areaTemplates?.map((template) => ({ ...template, a: transformPoint(template.a), b: transformPoint(template.b) })),
    fog: {
      ...fogWithoutMask,
      operations: scene.fog.operations.map((operation) => ({ ...operation, points: operation.points.map(transformPoint), radius: operation.radius === undefined ? undefined : operation.radius * scale })),
      explored: scalePolygonList(scene.fog.explored, scale, scale) ?? [],
    },
    waterRegions: scene.waterRegions?.map((region) => ({ ...region, points: region.points.map(transformPoint), polygons: scalePolygonList(region.polygons, scale, scale), flowPaths: scalePolygonList(region.flowPaths, scale, scale), erasePolygons: scalePolygonList(region.erasePolygons, scale, scale), brushRadii: region.brushRadii?.map((radius) => radius * scale) })),
    weatherDryRegions: scene.weatherDryRegions?.map((region) => ({ ...region, points: region.points.map(transformPoint), polygons: scalePolygonList(region.polygons, scale, scale) })),
    materialRegions: scene.materialRegions?.map((region) => ({ ...region, points: region.points.map(transformPoint), polygons: scalePolygonList(region.polygons, scale, scale) })),
    // These dimensions describe real objects on the table, not map artwork.
    physicalObjects: scene.physicalObjects?.map((object) => ({ ...object, position: transformPoint(object.position) })),
  };
}

/** Turning map play off leaves the authored map data intact for a later return. */
export function disableMediaMap(scene: Scene): Scene {
  return isMediaScene(scene) ? { ...scene, playableMap: false } : scene;
}
